import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api, type Decoder } from "../api";

const { Title, Paragraph, Text } = Typography;

// Decoders page — operator-facing toggle for the 32 built-in decoders.
//
// UX shape: toggles are local-only until the operator clicks Save.
// One Save click → one POST /api/v1/decoders/batch → one sfsensor
// restart. This avoids the original "toggle stacks N restarts" UX
// where flipping 5 decoders triggered 5 overlapping systemctl
// restarts and produced a 15-second blackout.
//
// Each enabled decoder runs Match() per flow and produces a separate
// InfluxDB series per (zone-node, sensor, direction). Disabling the
// noisy ones (NETBIOS, SLP, CHARGEN, MEMCACHED, etc.) is the
// cheapest way to cut chart cardinality and aggregator CPU on a
// busy backbone link.
export default function Decoders() {
  // Server-side state (last loaded). null = still loading.
  const [server, setServer] = useState<Decoder[] | null>(null);
  // Local pending state — id → enabled. Diff against `server` to know
  // what's dirty. Reset on successful Save / Reset.
  const [pending, setPending] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const data = await api.decoders();
      setServer(data);
      setPending({});
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Effective state: pending override falls back to server state.
  const effective = useMemo<Decoder[]>(() => {
    if (!server) return [];
    return server.map((d) => ({
      ...d,
      enabled: pending[d.id] ?? d.enabled,
    }));
  }, [server, pending]);

  // Diff stats: how many flipped on, how many flipped off vs server.
  const diff = useMemo(() => {
    let on = 0;
    let off = 0;
    if (!server) return { on, off, total: 0 };
    for (const d of server) {
      if (pending[d.id] === undefined) continue;
      if (pending[d.id] === d.enabled) continue;
      if (pending[d.id]) on++;
      else off++;
    }
    return { on, off, total: on + off };
  }, [server, pending]);

  function flip(id: number, next: boolean) {
    setPending((prev) => {
      const out = { ...prev };
      // If the new value matches server, drop the entry to keep the
      // pending map minimal (not strictly required, just tidier).
      const orig = server?.find((d) => d.id === id);
      if (orig && orig.enabled === next) {
        delete out[id];
      } else {
        out[id] = next;
      }
      return out;
    });
  }

  function reset() {
    setPending({});
  }

  async function save() {
    if (!server) return;
    setSaving(true);
    try {
      const enabledIDs = effective
        .filter((d) => d.enabled)
        .map((d) => d.id);
      const r = await api.saveDecoders(enabledIDs);
      message.success(
        `Saved: ${r.enabled_count} decoders enabled — sfsensor restarting (~3 s data gap)`
      );
      // Refresh from server so dirty state clears even if the user
      // had pending changes that matched the server (defensive).
      await load();
    } catch (e) {
      message.error(String(e));
    } finally {
      setSaving(false);
    }
  }

  const dirty = diff.total > 0;

  const columns: ColumnsType<Decoder> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 60,
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: "ascend",
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 160,
      render: (name: string, row: Decoder) => {
        const orig = server?.find((d) => d.id === row.id);
        const isDirty =
          orig !== undefined && pending[row.id] !== undefined &&
          pending[row.id] !== orig.enabled;
        return (
          <Space>
            {row.color ? (
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: row.color,
                  borderRadius: 2,
                  border: "1px solid var(--line)",
                }}
              />
            ) : null}
            <Text strong>{name}</Text>
            {row.id === 1 && <Tag color="blue">Catch-all</Tag>}
            {isDirty && <Tag color="gold">unsaved</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "enabled",
      width: 110,
      render: (enabled: boolean, row: Decoder) => (
        <Switch
          checked={enabled}
          // ID=1 (IP catch-all) is the global chart's WHERE clause —
          // disabling it would silently blank every dashboard. The
          // backend ignores the toggle for IDIP regardless, so we
          // also disable the UI control to make the contract visible.
          disabled={row.id === 1 || saving}
          onChange={(checked) => flip(row.id, checked)}
        />
      ),
    },
    {
      title: "Notes",
      key: "notes",
      render: (_: unknown, row: Decoder) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {decoderHint(row.name)}
        </Text>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Decoders</Title>
      <Paragraph type="secondary">
        Built-in protocol classifiers. Each enabled decoder runs Match()
        on every flow and produces a separate InfluxDB series per
        (zone-node, sensor, direction). Disable the ones you don't need
        to cut CPU and series cardinality. Catch-all <Tag>IP</Tag> cannot
        be disabled — the global chart depends on it.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Toggles are local until you click Save."
        description="One Save click triggers a single sfsensor restart (~3 s data gap), regardless of how many decoders you flipped. Use Reset to revert all pending changes."
      />

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load decoders"
          description={error}
        />
      )}

      <Card
        size="small"
        title={
          <Space>
            <span>Decoder list</span>
            {dirty && (
              <Badge
                count={diff.total}
                style={{ backgroundColor: "var(--amber)" }}
                title={`${diff.on} to enable, ${diff.off} to disable`}
              />
            )}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={load}
              disabled={saving}
            >
              Refresh
            </Button>
            <Button
              icon={<UndoOutlined />}
              size="small"
              onClick={reset}
              disabled={!dirty || saving}
            >
              Reset
            </Button>
            <Popconfirm
              title="Save decoder changes?"
              description={`${diff.on} to enable, ${diff.off} to disable. sfsensor will restart (~3 s data gap).`}
              onConfirm={save}
              disabled={!dirty || saving}
              okText="Save & restart"
              cancelText="Cancel"
            >
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size="small"
                disabled={!dirty}
                loading={saving}
              >
                Save
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Table<Decoder>
          rowKey="id"
          columns={columns}
          dataSource={effective}
          loading={server === null && error === null}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
}

// decoderHint returns a one-line operator-facing tooltip for each
// known decoder name. Fallback empty so unknown future decoders just
// render with no note rather than an undefined string.
function decoderHint(name: string): string {
  const m: Record<string, string> = {
    IP: "All IP traffic. Required for the global chart.",
    TCP: "Any TCP. High volume — usually leave on.",
    "TCP+SYN": "TCP SYN no ACK. SYN-flood detection.",
    UDP: "Any UDP. Usually leave on.",
    ICMP: "ICMP + ICMPv6.",
    DNS: "UDP/53. Useful on resolvers; turn off on transit links.",
    NTP: "UDP/123. Reflection-attack signal.",
    SSDP: "UDP/1900 reflection.",
    LDAP: "UDP/389.",
    CLDAP: "UDP/389 amplification.",
    CHARGEN: "UDP/19 reflection. Almost always 0.",
    HTTP: "TCP/80.",
    HTTPS: "TCP/443.",
    OTHER: "Non-TCP/UDP/ICMP IP — GRE, ESP, etc.",
    MAIL: "SMTP / POP3 / IMAP family. Off on backbone links.",
    IPSEC: "IP proto 50 (ESP) or 51 (AH).",
    SSH: "TCP/22.",
    SIP: "UDP/5060–5061.",
    SNMP: "UDP/161–162.",
    RDP: "TCP/3389.",
    "FLOW+SYN": "Same predicate as TCP+SYN, kept for Wansight parity.",
    "TCP-NULL": "TCP with all flag bits clear — anomalous.",
    "TCP+RST": "TCP RST. Spike → connection-reset attack.",
    "TCP+ACK": "TCP ACK no SYN. Bulk transfer signal.",
    "TCP+SYNACK": "TCP SYN+ACK. Server response volume.",
    "TCP-ALL": "Christmas-tree TCP — all 6 flags set, anomaly.",
    NETBIOS: "Ports 137–139.",
    QUIC: "UDP/443. Best-effort — overlaps HTTPS.",
    MEMCACHED: "UDP/11211. Famous reflection vector.",
    SLP: "UDP/427.",
    GRE: "IP proto 47.",
    INVALID: "Anomalous src/dst port combos (port 0, src=dst).",
  };
  return m[name] ?? "";
}
